import { db, auth } from './firebase';
import { 
  collection, 
  getDocs, 
  doc, 
  deleteDoc, 
  updateDoc,
  addDoc,
  query,
  where,
  setDoc
} from 'firebase/firestore';
import { hashPassword } from './utils';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';

// Kullanıcı Yönetimi
export const getAllUsers = async () => {
  const usersRef = collection(db, 'users');
  const snapshot = await getDocs(usersRef);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

export const deleteUser = async (userId) => {
  await deleteDoc(doc(db, 'users', userId));
};

export const updateUserStatus = async (userId, isActive) => {
  await updateDoc(doc(db, 'users', userId), {
    isActive
  });
};

// Kelime Yönetimi
export const getWords = async () => {
  try {
    const wordsRef = collection(db, 'words');
    const snapshot = await getDocs(wordsRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Kelime getirme hatası:', error);
    throw error;
  }
};

export const addWord = async (wordData) => {
  try {
    await checkAdminAuth();
    
    const data = {
      ...wordData,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    await addDoc(collection(db, 'words'), data);
  } catch (error) {
    console.error('Kelime ekleme hatası:', error);
    throw error;
  }
};

export const updateWord = async (wordId, wordData) => {
  try {
    await checkAdminAuth();
    
    const data = {
      ...wordData,
      updatedAt: new Date()
    };
    
    await updateDoc(doc(db, 'words', wordId), data);
  } catch (error) {
    console.error('Kelime güncelleme hatası:', error);
    throw error;
  }
};

export const deleteWord = async (wordId) => {
  await deleteDoc(doc(db, 'words', wordId));
};

// Puan Yönetimi
export const getScores = async () => {
  const usersRef = collection(db, 'users');
  const snapshot = await getDocs(usersRef);
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    .sort((a, b) => b.score - a.score);
};

// Referans Kodu Yönetimi
export const createReferralCode = async (referralData) => {
  await addDoc(collection(db, 'referralCodes'), referralData);
};

export const getReferralCodes = async () => {
  const codesRef = collection(db, 'referralCodes');
  const snapshot = await getDocs(codesRef);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

export const getReferralStats = async (referralId) => {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('referralCode', '==', referralId));
  const snapshot = await getDocs(q);
  const users = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
  
  return {
    usageCount: users.length,
    users
  };
};

// Admin Kullanıcı Yönetimi
export const getAdminUsers = async () => {
  const usersRef = collection(db, 'adminUsers');
  const snapshot = await getDocs(usersRef);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

export const createAdminUser = async (userData) => {
  // Şifreyi hashle ve güvenli bir şekilde sakla
  const hashedPassword = await hashPassword(userData.password);
  await addDoc(collection(db, 'adminUsers'), {
    ...userData,
    password: hashedPassword,
    createdAt: new Date()
  });
};

export const updateAdminUser = async (userId, userData) => {
  const updateData = { ...userData };
  if (userData.password) {
    // Yeni şifre varsa hashle
    updateData.password = await hashPassword(userData.password);
  } else {
    // Şifre değişmiyorsa password alanını kaldır
    delete updateData.password;
  }
  await updateDoc(doc(db, 'adminUsers', userId), updateData);
};

export const deleteAdminUser = async (userId) => {
  await deleteDoc(doc(db, 'adminUsers', userId));
};

// Varsayılan admin kullanıcısını oluştur
export const createDefaultAdmin = async () => {
  try {
    // 1. Önce Authentication'daki admin kullanıcısını oluştur
    let adminUser;
    try {
      // Önce mevcut kullanıcıyı silmeyi dene
      const existingUsers = await getDocs(
        query(collection(db, 'adminUsers'))
      );
      
      for (const doc of existingUsers.docs) {
        await deleteDoc(doc.ref);
      }

      // Yeni admin kullanıcısı oluştur
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        'admin@airup.com', 
        'Admin2024!'
      );
      adminUser = userCredential.user;
      console.log('Admin Authentication kullanıcısı oluşturuldu');
    } catch (authError) {
      if (authError.code === 'auth/email-already-in-use') {
        // Eğer kullanıcı zaten varsa, giriş yap
        const userCredential = await signInWithEmailAndPassword(
          auth, 
          'admin@airup.com', 
          'Admin2024!'
        );
        adminUser = userCredential.user;
        console.log('Mevcut admin kullanıcısına giriş yapıldı');
      } else {
        throw authError;
      }
    }

    // 2. Firestore'da admin kaydını oluştur
    const adminData = {
      uid: adminUser.uid,
      email: adminUser.email,
      username: 'superadmin',
      role: 'admin',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Dokümanı UID ile oluştur
    await setDoc(doc(db, 'adminUsers', adminUser.uid), adminData);
    console.log('Admin Firestore kaydı oluşturuldu');

    return { 
      success: true, 
      message: 'Admin kullanıcısı başarıyla oluşturuldu',
      user: adminUser 
    };

  } catch (error) {
    console.error('Admin oluşturma hatası:', error);
    throw error;
  }
};

// Seviye Yönetimi
export const getLevels = async () => {
  const levelsRef = collection(db, 'levels');
  const snapshot = await getDocs(levelsRef);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

export const addLevel = async (levelData) => {
  try {
    // Admin kontrolü
    await checkAdminAuth();
    
    const data = {
      ...levelData,
      requiredScore: parseInt(levelData.requiredScore, 10) || 0,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    await addDoc(collection(db, 'levels'), data);
    console.log('Seviye başarıyla eklendi');
  } catch (error) {
    console.error('Seviye ekleme hatası:', error);
    throw error;
  }
};

export const updateLevel = async (levelId, levelData) => {
  const data = {
    ...levelData,
    requiredScore: parseInt(levelData.requiredScore, 10) || 0
  };
  await updateDoc(doc(db, 'levels', levelId), data);
};

export const deleteLevel = async (levelId) => {
  await deleteDoc(doc(db, 'levels', levelId));
};

// Veritabanını sıfırlama fonksiyonu
export const resetDatabase = async () => {
  try {
    // 1. Tüm koleksiyonları temizle
    const collections = ['words', 'levels', 'users', 'adminUsers', 'referralCodes'];
    
    for (const collectionName of collections) {
      const collectionRef = collection(db, collectionName);
      const snapshot = await getDocs(collectionRef);
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      console.log(`${collectionName} koleksiyonu temizlendi`);
    }

    // 2. Yeni admin kullanıcısını oluştur
    const result = await createDefaultAdmin();
    console.log('Yeni admin kullanıcısı oluşturuldu:', result);

    return { 
      success: true, 
      message: 'Veritabanı başarıyla sıfırlandı ve yeni admin oluşturuldu' 
    };
  } catch (error) {
    console.error('Veritabanı sıfırlama hatası:', error);
    throw error;
  }
};

// Admin kontrolü için yardımcı fonksiyon
export const checkAdminAuth = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Lütfen önce giriş yapın');
    }

    const adminUsersRef = collection(db, 'adminUsers');
    const q = query(adminUsersRef, where('uid', '==', currentUser.uid));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error('Bu işlem için admin yetkisi gerekiyor');
    }

    return true;
  } catch (error) {
    console.error('Admin yetki kontrolü hatası:', error);
    throw error;
  }
}; 